import moment from "moment-timezone";
import { parsePhoneNumberFromString } from "libphonenumber-js/min";
import metadata from "libphonenumber-js/min/metadata";

/**
 * Builds the JSON `change_column_value` takes for each editable type. Formats were probed live
 * against API 2025-10: `{}` clears every type except checkbox (rejected; send `{checked:false}`),
 * and JSON `null` is never accepted. monday accepts some bad values silently (a reversed
 * timeline, a rating over the max, a dropdown id that doesn't exist), so those are guarded here.
 */
export type BuildResult = { ok: true; value: unknown } | { ok: false; error: string };

export interface PersonOrTeam {
  id: number;
  kind: "person" | "team";
}

const ok = (value: unknown): BuildResult => ({ ok: true, value });
const fail = (error: string): BuildResult => ({ ok: false, error });

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const HOUR_PATTERN = /^([01]?\d|2[0-3]):([0-5]\d)$/;

export const parseRawValue = (raw: string | null | undefined): any => {
  if (raw == null) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export const buildTextValue = (text: string): BuildResult => ok(text);

export const buildLongTextValue = (text: string): BuildResult => ok(text ? { text } : {});

export const buildNumbersValue = (input: string): BuildResult => {
  const trimmed = input.trim();
  if (trimmed === "") return ok("");
  const number = Number(trimmed);
  return Number.isFinite(number) ? ok(trimmed) : fail("Enter a number.");
};

export const buildEmailValue = (email: string, label: string): BuildResult => {
  const address = email.trim();
  if (address === "") return ok({});
  if (!EMAIL_PATTERN.test(address)) return fail("Enter a valid email address.");
  return ok({ email: address, text: label.trim() || address });
};

/**
 * The country a "+" number belongs to. Numbers libphonenumber can't place exactly (test ranges,
 * new allocations) fall back to the calling code's main country, which monday accepts.
 */
const countryOfInternationalNumber = (number: string): string | undefined => {
  const parsed = parsePhoneNumberFromString(number);
  if (parsed?.country) return parsed.country;
  const callingCode = parsed?.countryCallingCode;
  return callingCode ? metadata.country_calling_codes[callingCode]?.[0] : undefined;
};

/**
 * monday needs a countryShortName that matches the number: with none, or a stale one, its
 * webhook reports the phone as blank and SIMB syncs that blank to every board (probed live).
 * A "+" number brings its own country; a local number keeps the column's current one.
 */
export const buildPhoneValue = (phone: string, currentCountry?: string): BuildResult => {
  // monday rejects a number with spaces, dashes, dots or brackets ("invalid value"), so send
  // just the digits and a leading "+".
  const number = phone.trim().replace(/[\s\-().]/g, "");
  if (number === "") return ok({});
  if (number.startsWith("+")) {
    const countryShortName = countryOfInternationalNumber(number);
    return countryShortName
      ? ok({ phone: number, countryShortName })
      : fail("Check the country code: it doesn't match any country.");
  }
  if (currentCountry) return ok({ phone: number, countryShortName: currentCountry });
  return fail("Start the number with + and its country code, e.g. +1 415 555 0100.");
};

export const buildLinkValue = (url: string, text: string): BuildResult => {
  const target = url.trim();
  if (target === "") return ok({});
  return ok({ url: target, text: text.trim() || target });
};

export const buildRatingValue = (rating: number, max: number): BuildResult => {
  if (rating === 0) return ok({});
  if (rating < 0 || rating > max) return fail(`Choose a rating from 1 to ${max}.`);
  return ok({ rating });
};

export const buildCheckboxValue = (checked: boolean): BuildResult => ok({ checked });

/**
 * monday stores a date's time in UTC and shows it in the user's monday profile time zone, which
 * can differ from the browser's. The editor works in that profile zone (`timeZone`, an IANA
 * name), falling back to the browser's when it isn't known.
 */
export const buildDateValue = (
  date: string,
  time: string,
  timeZone: string = moment.tz.guess(),
): BuildResult => {
  if (date === "") return time ? fail("Choose a date for this time.") : ok({});
  if (!time) return ok({ date });
  const zoned = moment.tz(`${date} ${time}`, "YYYY-MM-DD HH:mm", true, timeZone);
  if (!zoned.isValid()) return fail("Enter a valid date and time.");
  const utc = zoned.utc();
  return ok({ date: utc.format("YYYY-MM-DD"), time: utc.format("HH:mm:ss") });
};

export const parseDateValue = (
  value: { date?: string; time?: string | null } | null,
  timeZone: string = moment.tz.guess(),
): { date: string; time: string } => {
  if (!value?.date) return { date: "", time: "" };
  if (!value.time) return { date: value.date, time: "" };
  const zoned = moment.utc(`${value.date} ${value.time}`, "YYYY-MM-DD HH:mm:ss").tz(timeZone);
  return { date: zoned.format("YYYY-MM-DD"), time: zoned.format("HH:mm") };
};

export const buildHourValue = (time: string): BuildResult => {
  if (time === "") return ok({});
  const match = HOUR_PATTERN.exec(time);
  if (!match) return fail("Enter a valid time.");
  return ok({ hour: Number(match[1]), minute: Number(match[2]) });
};

export const buildTimelineValue = (from: string, to: string): BuildResult => {
  if (!from && !to) return ok({});
  if (!from || !to) return fail("Choose both a start and an end date.");
  return ok(from <= to ? { from, to } : { from: to, to: from });
};

export const buildStatusValue = (index: number | null): BuildResult =>
  ok(index === null ? {} : { index });

export const buildSelectionValue = (type: "dropdown" | "tags", ids: number[]): BuildResult => {
  if (ids.length === 0) return ok({});
  return ok(type === "dropdown" ? { ids } : { tag_ids: ids });
};

export const buildPeopleValue = (personsAndTeams: PersonOrTeam[]): BuildResult =>
  ok(personsAndTeams.length ? { personsAndTeams } : {});
